import axios from 'axios';
import { clearImpersonationState } from './utils/impersonation';

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.workifypl.com';

const api = axios.create({
  baseURL: API_BASE,
  timeout: 20000,
  headers: { 'Content-Type': 'application/json' },
});

let isAuthenticating = false;
let lastHealthCheck = { ok: null, timestamp: 0, latency: null };

// ══════════════════════════════════════════════════════════════
// Healthcheck
// ══════════════════════════════════════════════════════════════

export async function checkHealth() {
  const start = Date.now();
  try {
    const res = await axios.get(`${API_BASE}/health`, { timeout: 5000 });
    lastHealthCheck = {
      ok: res.status === 200,
      timestamp: Date.now(),
      latency: Date.now() - start,
      data: res.data,
    };
    return lastHealthCheck;
  } catch (e) {
    lastHealthCheck = {
      ok: false,
      timestamp: Date.now(),
      latency: Date.now() - start,
      error: e.message,
    };
    return lastHealthCheck;
  }
}

export function getLastHealthCheck() {
  return lastHealthCheck;
}

// ══════════════════════════════════════════════════════════════
// Request interceptor
// ══════════════════════════════════════════════════════════════

api.interceptors.request.use(cfg => {
  const token = sessionStorage.getItem('jwt');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  
  // Add request ID for tracking
  cfg.headers['X-Request-ID'] = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  
  // Log request (dev)
  if (import.meta.env.DEV) {
    console.log(`[API] ${cfg.method?.toUpperCase()} ${cfg.url}`);
  }
  
  return cfg;
});

// ══════════════════════════════════════════════════════════════
// Response interceptor — detailed error mapping
// ══════════════════════════════════════════════════════════════

const ERROR_MESSAGES = {
  // Network errors
  'NETWORK_ERROR': 'Сеть недоступна. Проверьте подключение к интернету.',
  'TIMEOUT': 'Сервер не отвечает. Повторите через 10 секунд.',
  'DNS_ERROR': 'Не удалось подключиться к серверу. Проверьте соединение.',
  
  // Auth errors
  'AUTH_TELEGRAM': 'Ошибка авторизации Telegram. Откройте мини-приложение заново из бота /start.',
  'AUTH_EXPIRED': 'Сессия истекла. Перезапустите приложение.',
  'AUTH_FORBIDDEN': 'Нет прав для этого действия.',
  
  // Validation
  'VALIDATION': 'Проверьте правильность заполнения.',
  'PROFILE_NOT_VERIFIED': 'Профиль не верифицирован. Обратитесь к администратору.',
  'GATEWAY_EXISTS': 'Gateway договор уже подписан.',
  'NO_COMPANY': 'Не назначена фирма.',
  'TERMS_NOT_ACCEPTED': 'Необходимо принять все условия договора.',
  
  // Server errors
  'SERVER_ERROR': 'Ошибка сервера. Мы уже записали инцидент.',
  'ENDPOINT_NOT_FOUND': 'Ошибка конфигурации: endpoint не найден.',
};

function classifyError(error) {
  // Log full error for debugging
  console.error('[API] Full error:', error);
  console.error('[API] Response:', error.response);
  console.error('[API] Message:', error.message);
  
  // No response — network/connection issue
  if (!error.response) {
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      return { type: 'TIMEOUT', code: 0 };
    }
    if (error.message?.includes('Network Error')) {
      return { type: 'NETWORK_ERROR', code: 0 };
    }
    // Check if it's actually a response error that axios didn't parse correctly
    if (error.request?.status) {
      return { type: 'SERVER_ERROR', code: error.request.status };
    }
    return { type: 'DNS_ERROR', code: 0 };
  }
  
  const status = error.response.status;
  const detail = error.response.data?.detail?.toLowerCase() || '';
  
  // Auth errors (401/403)
  if (status === 401) {
    if (detail.includes('telegram') || detail.includes('initdata') || detail.includes('auth')) {
      return { type: 'AUTH_TELEGRAM', code: 401 };
    }
    return { type: 'AUTH_EXPIRED', code: 401 };
  }
  
  if (status === 403) {
    if (detail.includes('verified') || detail.includes('верифиц')) {
      return { type: 'PROFILE_NOT_VERIFIED', code: 403 };
    }
    return { type: 'AUTH_FORBIDDEN', code: 403 };
  }
  
  // Not found
  if (status === 404) {
    return { type: 'ENDPOINT_NOT_FOUND', code: 404 };
  }
  
  // Conflict (already exists)
  if (status === 409 || detail.includes('already') || detail.includes('уже подписан')) {
    return { type: 'GATEWAY_EXISTS', code: 409 };
  }
  
  // Validation
  if (status === 400 || status === 422) {
    if (detail.includes('условия') || detail.includes('terms') || detail.includes('accept')) {
      return { type: 'TERMS_NOT_ACCEPTED', code: status };
    }
    if (detail.includes('фирма') || detail.includes('company')) {
      return { type: 'NO_COMPANY', code: status };
    }
    return { type: 'VALIDATION', code: status };
  }
  
  // Server error
  if (status >= 500) {
    return { type: 'SERVER_ERROR', code: status };
  }
  
  return { type: 'UNKNOWN', code: status };
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const classification = classifyError(error);
    const requestId = error.config?.headers?.['X-Request-ID'] || error.response?.data?.request_id;
    const serverDetail = error.response?.data?.detail;
    
    // Set user-friendly message
    let userMessage = ERROR_MESSAGES[classification.type] || serverDetail || 'Произошла ошибка.';
    
    // Add request ID for server errors
    if (classification.type === 'SERVER_ERROR' && requestId) {
      userMessage = `${userMessage} Код: ${requestId}`;
    }
    
    // Enrich error object
    error.userMessage = userMessage;
    error.errorType = classification.type;
    error.errorCode = classification.code;
    error.requestId = requestId;
    error.serverDetail = serverDetail;
    
    // Log error
    console.error(`[API Error] ${classification.type} (${classification.code})`, {
      url: error.config?.url,
      requestId,
      detail: serverDetail,
    });
    
    // Clear auth on 401 and try to re-authenticate (skip during impersonation)
    if (classification.code === 401 && !error.config?._retry && localStorage.getItem('impersonating') !== 'true' && !(error.config?.url || '').includes('/auth/telegram')) {
      sessionStorage.removeItem('jwt');
      // Try to re-authenticate via Telegram
      try {
        const tg = window.Telegram?.WebApp;
        if (tg?.initData && tg.initData.length > 10) {
          const { data } = await api.post('/auth/telegram', { init_data: tg.initData });
          sessionStorage.setItem('jwt', data.access_token);
          error.config._retry = true;
          error.config.headers.Authorization = `Bearer ${data.access_token}`;
          return api(error.config);
        }
      } catch (reAuthError) {
        console.error('[API] Re-auth failed:', reAuthError);
      }
      // Clear web token too if expired
      localStorage.removeItem('web_jwt');
    }

    return Promise.reject(error);
  }
);

// ══════════════════════════════════════════════════════════════
// Authentication
// ══════════════════════════════════════════════════════════════

export async function authenticate() {
  // If impersonating, skip Telegram auth — use existing JWT
  if (localStorage.getItem('impersonating') === 'true' && sessionStorage.getItem('jwt')) {
    try {
      const { data } = await api.get('/me');
      return data;
    } catch (e) {
      // Token expired — fall back to normal auth
      clearImpersonationState();
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
    }
  }

  const tg = window.Telegram?.WebApp;

  // 1. Telegram WebApp auth (existing flow)
  if (tg && tg.initData && tg.initData.length >= 10) {
    isAuthenticating = true;
    try {
      const { data } = await api.post('/auth/telegram', { init_data: tg.initData });
      sessionStorage.setItem('jwt', data.access_token);
      return data.user;
    } catch (e) {
      if (e.errorType === 'AUTH_TELEGRAM' || e.response?.status === 401) {
        // Continue to fallback path below (for Telegram WebView quirks)
      } else {
        throw e;
      }
    } finally {
      isAuthenticating = false;
    }
  }

  // 1b. Telegram fallback: if initData is missing/broken but initDataUnsafe.user exists,
  // send minimal payload (server allows only superadmin-safe fallback).
  if (tg?.initDataUnsafe?.user?.id) {
    try {
      const unsafeInit = `user=${encodeURIComponent(JSON.stringify(tg.initDataUnsafe.user))}&auth_date=${Math.floor(Date.now()/1000)}`;
      const { data } = await api.post('/auth/telegram', { init_data: unsafeInit });
      sessionStorage.setItem('jwt', data.access_token);
      return data.user;
    } catch (_) {
      // fall through to web auth
    }
  }

  // 2. Web auth — check for stored JWT in localStorage
  const webToken = localStorage.getItem('web_jwt');
  if (webToken) {
    sessionStorage.setItem('jwt', webToken);
    try {
      const { data } = await api.get('/me');
      return data;
    } catch (e) {
      // Token expired — clear and show login
      localStorage.removeItem('web_jwt');
      sessionStorage.removeItem('jwt');
    }
  }

  // 3. No auth available — signal web login needed
  throw new Error('NO_AUTH');
}

// ══════════════════════════════════════════════════════════════
// Diagnostic info (for admin)
// ══════════════════════════════════════════════════════════════

export function webLogout() {
  localStorage.removeItem('web_jwt');
  sessionStorage.removeItem('jwt');
  window.location.reload();
}

export function getDiagnosticInfo() {
  const tg = window.Telegram?.WebApp;
  return {
    apiBaseUrl: API_BASE,
    webappOrigin: window.location.origin,
    telegramAvailable: !!tg,
    initDataLength: tg?.initData?.length || 0,
    jwtPresent: !!sessionStorage.getItem('jwt'),
    lastHealthCheck,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
  };
}

export default api;
